import { useState, useEffect, useRef } from "react";
import { usePausableInterval } from "../hooks/usePausableInterval";
import axios from "axios";
import { Bell, Check, X } from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
import { getLayoutBootstrap, isLayoutBootWindow } from "../lib/layoutBootstrap"; // B1099

const TYPE_STYLES = {
  new_user: { bg: "bg-blue-50", dot: "bg-blue-500" },
  certificate: { bg: "bg-amber-50", dot: "bg-amber-500" },
  pathway_complete: { bg: "bg-blue-50", dot: "bg-blue-500" },
  step_failed: { bg: "bg-red-50", dot: "bg-red-500" },
  default: { bg: "bg-zinc-50", dot: "bg-zinc-400" },
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    // Vincent B667 (Eric 2026-08-05 SPEC STRICK PROD) — Les 4 garde-fous
    // (inFlight + hidden + uploadActive + backoff) sont maintenant gérés
    // par le hook usePausableInterval — ce callback ne fait que la requête.
    // B1099 (Eric 2026-09-07) — Le PREMIER chargement vient de l'appel
    // agrégé du gabarit ; les sondes suivantes (30 s) et l'ouverture du
    // panneau gardent /api/notifications.
    try {
      if (isLayoutBootWindow()) {
        const boot = await getLayoutBootstrap();
        if (boot?.notifications) {
          setNotifications(boot.notifications.notifications || []);
          setUnread(boot.notifications.unread_count || 0);
          return true;
        }
      }
      const { data } = await axios.get(`${API}/api/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
      return true;
    } catch {
      return false;
    }
  };

  usePausableInterval(loadNotifications, 30000);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/api/notifications/read-all`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (err) {
      // Silent fail
    }
  };

  const styles = (type) => TYPE_STYLES[type] || TYPE_STYLES.default;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open) loadNotifications(); }}
        className="relative p-2 hover:bg-zinc-100 rounded-sm"
        data-testid="notifications-bell-btn"
      >
        <Bell size={20} className="text-zinc-600" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-600 text-white text-[10px] font-bold rounded-full px-1" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-zinc-200 rounded-sm shadow-lg z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-medium text-sm">Notifications</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#002FA7] hover:underline" data-testid="mark-all-read-btn">
                Tout marquer comme lu
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-400">Aucune notification</div>
            ) : (
              notifications.slice(0, 15).map((n) => {
                const s = styles(n.type);
                return (
                  <div
                    key={n.id}
                    className={`px-4 py-3 border-b border-zinc-100 ${!n.read ? s.bg : ""} transition-colors`}
                    data-testid={`notif-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className={`w-2 h-2 rounded-full ${s.dot} flex-shrink-0 mt-1.5`} />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{n.title}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{n.message}</div>
                        <div className="text-[10px] text-zinc-400 mt-1">
                          {new Date(n.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
